// src/components/CommentiAttivita.js
// Commenti di attività e sottoattività (W440), con foto e file allegati che restano legati al
// commento che li accompagna. Un commento si può modificare solo da chi l'ha scritto: ogni
// modifica resta registrata (testo di prima, allegati aggiunti e tolti, chi e quando) e il
// commento mostra «✎ modificato», con lo storico a un clic. I file tolti in una modifica
// restano nell'archivio e si aprono dallo storico; si cancellano solo eliminando il commento.
// Scrivere un commento avvisa chi segue l'attività (campanella in Nexiora, non email).
// Le sottoattività usano lo stesso elenco «comments»: taskId = attività madre (le regole
// Firestore controllano quella), subtaskId = la sottoattività.
import React, { useEffect, useRef, useState } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { Trash2, Send, Paperclip, Camera, Pencil, X } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { messaggioErrore } from "../utils/erroriUtente";
import { caricaAllegati, eliminaAllegati, controllaFile, eImmagine, iconaFile, dimensioneLeggibile, ACCETTA_FILE, MAX_FILE_PER_COMMENTO } from "../utils/allegatiCommento";

const quando = (ts) => { const d = ts?.toDate?.() ?? (ts ? new Date(ts) : null); return d && !isNaN(d) ? format(d, "d MMM HH:mm", { locale: it }) : ""; };

function Allegati({ allegati = [], onTogli }) {
  if (!allegati.length) return null;
  const foto = allegati.filter(eImmagine), file = allegati.filter(a => !eImmagine(a));
  return (
    <div style={{ marginTop: 6 }}>
      {foto.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: file.length ? 6 : 0 }}>
          {foto.map((a, i) => (
            <span key={a.path || a.url || i} style={{ position: "relative" }}>
              <a href={a.url} target="_blank" rel="noopener noreferrer" title={a.nome}>
                <img src={a.url} alt={a.nome} loading="lazy" style={{ width: 84, height: 84, objectFit: "cover", borderRadius: 8, border: "1px solid #e5e7eb", display: "block" }} />
              </a>
              {onTogli && <button type="button" onClick={() => onTogli(a)} aria-label={`Togli ${a.nome}`} style={{ position: "absolute", top: 2, right: 2, background: "rgba(0,0,0,.6)", color: "#fff", border: "none", borderRadius: 10, width: 20, height: 20, cursor: "pointer", fontSize: 11 }}>✕</button>}
            </span>
          ))}
        </div>
      )}
      {file.map((a, i) => (
        <div key={a.path || a.url || i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, marginBottom: 3 }}>
          <a href={a.url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 7, padding: "3px 8px", color: "#1f2937", textDecoration: "none", maxWidth: "100%" }}>
            <span>{iconaFile(a.nome)}</span><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.nome}</span>
            {a.dimensione ? <span style={{ color: "#9ca3af", flexShrink: 0 }}>{dimensioneLeggibile(a.dimensione)}</span> : null}
          </a>
          {onTogli && <button type="button" onClick={() => onTogli(a)} aria-label={`Togli ${a.nome}`} className="btn-icon" style={{ padding: 2 }}><X size={12} /></button>}
        </div>
      ))}
    </div>
  );
}

// Scelta foto/file e file in attesa di partire (nuovo commento o modifica)
function ScegliFile({ inAttesa, setInAttesa, giaAllegati = 0, disabilitato }) {
  const { addToast } = useToast();
  const foto = useRef(null), file = useRef(null);
  const aggiungi = (lista) => {
    const nuovi = [];
    for (const f of Array.from(lista || [])) {
      const err = controllaFile(f);
      if (err) { addToast(err, "error"); continue; }
      nuovi.push(f);
    }
    setInAttesa(v => {
      const tutti = [...v, ...nuovi];
      if (tutti.length + giaAllegati > MAX_FILE_PER_COMMENTO) { addToast(`Al massimo ${MAX_FILE_PER_COMMENTO} allegati per commento`, "error"); return tutti.slice(0, Math.max(0, MAX_FILE_PER_COMMENTO - giaAllegati)); }
      return tutti;
    });
  };
  return (
    <>
      <input ref={foto} type="file" accept="image/*" multiple hidden onChange={e => { aggiungi(e.target.files); e.target.value = ""; }} aria-label="Scegli foto" />
      <input ref={file} type="file" accept={ACCETTA_FILE} multiple hidden onChange={e => { aggiungi(e.target.files); e.target.value = ""; }} aria-label="Scegli file" />
      <button type="button" className="btn btn-secondary btn-sm" disabled={disabilitato} onClick={() => foto.current?.click()} title="Foto dalla fotocamera o dalla galleria" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Camera size={13} /> Foto</button>
      <button type="button" className="btn btn-secondary btn-sm" disabled={disabilitato} onClick={() => file.current?.click()} title="PDF, Word, Excel e altri file (max 20 MB)" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Paperclip size={13} /> File</button>
      {inAttesa.length > 0 && (
        <div style={{ flexBasis: "100%", display: "flex", flexWrap: "wrap", gap: 5 }}>
          {inAttesa.map((f, i) => (
            <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, background: "#f5f3ff", border: "1px solid #ddd6fe", color: "#5b21b6", borderRadius: 7, padding: "2px 7px" }}>
              {iconaFile(f.name)} {f.name} <span style={{ color: "#9ca3af" }}>{dimensioneLeggibile(f.size)}</span>
              <button type="button" onClick={() => setInAttesa(v => v.filter((_, j) => j !== i))} aria-label={`Togli ${f.name}`} style={{ background: "none", border: "none", cursor: "pointer", color: "#6d28d9", padding: 0 }}>✕</button>
            </span>
          ))}
        </div>
      )}
    </>
  );
}

function Storico({ voci = [], onChiudi }) {
  return (
    <div role="region" aria-label="Storico modifiche del commento" style={{ marginTop: 6, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 10px", fontSize: 11.5, color: "#78350f" }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
        <b>Storico delle modifiche</b>
        <button type="button" onClick={onChiudi} className="btn-icon" aria-label="Chiudi storico" style={{ marginLeft: "auto", padding: 2 }}><X size={12} /></button>
      </div>
      {[...voci].reverse().map((v, i) => (
        <div key={i} style={{ borderTop: i ? "1px dashed #fde68a" : "none", paddingTop: i ? 6 : 0, marginTop: i ? 6 : 0 }}>
          <div style={{ fontWeight: 700 }}>{quando(v.il)} · {v.daNome || "—"}</div>
          {v.testoPrima !== undefined && v.testoPrima !== v.testoDopo && (
            <div style={{ marginTop: 2 }}>Testo di prima: <span style={{ whiteSpace: "pre-wrap", textDecoration: "line-through", color: "#92400e" }}>{v.testoPrima || "(vuoto)"}</span></div>
          )}
          {(v.allegatiAggiunti || []).length > 0 && <div style={{ marginTop: 2 }}>Aggiunti: {v.allegatiAggiunti.join(", ")}</div>}
          {(v.allegatiTolti || []).length > 0 && (
            <div style={{ marginTop: 2 }}>Tolti: {v.allegatiTolti.map((a, j) => <React.Fragment key={j}>{j ? ", " : ""}<a href={a.url} target="_blank" rel="noopener noreferrer" style={{ color: "#92400e" }}>{a.nome}</a></React.Fragment>)}</div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * taskId: attività (per una sottoattività, l'attività madre) · subtaskId: la sottoattività, se è lei
 * task: l'attività o la sottoattività, per sapere chi avvisare · orgId · projectId
 */
export default function CommentiAttivita({ taskId, subtaskId = "", task = null, orgId, projectId = null, commessaChiusa = false }) {
  const { currentUser, userProfile } = useAuth();
  const { addToast } = useToast();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [inAttesa, setInAttesa] = useState([]);
  const [invio, setInvio] = useState(false);
  const [modifica, setModifica] = useState(null); // { id, testo, allegati, inAttesa }
  const [storicoAperto, setStoricoAperto] = useState(null);

  useEffect(() => {
    if (!taskId) return undefined;
    return onSnapshot(query(collection(db, "comments"), where("taskId", "==", taskId)), snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => (c.subtaskId || "") === (subtaskId || ""));
      list.sort((a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0));
      setComments(list);
    });
  }, [taskId, subtaskId]);

  const avvisa = async (testo, nAllegati) => {
    try {
      const chi = new Set();
      if (task?.assignedTo) chi.add(task.assignedTo);
      if (task?.createdBy) chi.add(task.createdBy);
      comments.forEach(c => { if (c.authorId) chi.add(c.authorId); });
      (task?.condivisaCon || []).forEach(u => chi.add(u));
      chi.delete(currentUser.uid);
      if (!orgId || !chi.size) return;
      const base = testo ? (testo.length > 90 ? testo.slice(0, 90) + "…" : testo) : "";
      const anteprima = [base, nAllegati ? `📎 ${nAllegati} allegat${nAllegati === 1 ? "o" : "i"}` : ""].filter(Boolean).join(" · ");
      const titoloRif = task?.title || (subtaskId ? "una sottoattività" : "un'attività");
      await Promise.all([...chi].map(uid => addDoc(collection(db, "notifiche"), {
        userId: uid, orgId, tipo: "commento_attivita", titolo: "Nuovo commento",
        testo: `${currentUser.displayName || "Un collega"} ha commentato «${titoloRif}»: ${anteprima}`,
        projectId: projectId || task?.projectId || null, taskId,
        ...(subtaskId ? { rifCollezione: "subtasks", rifId: subtaskId } : {}),
        letta: false, createdAt: serverTimestamp(),
      })));
    } catch (err) { console.error("Commento salvato, ma notifica non inviata:", err); }
  };

  const invia = async (e) => {
    e.preventDefault();
    const testo = text.trim();
    if (commessaChiusa || (!testo && !inAttesa.length) || invio) return;
    setInvio(true);
    try {
      const allegati = inAttesa.length ? await caricaAllegati(inAttesa, { orgId, taskId }) : [];
      await addDoc(collection(db, "comments"), {
        taskId, ...(subtaskId ? { subtaskId } : {}), text: testo, allegati,
        authorId: currentUser.uid, authorName: currentUser.displayName, createdAt: serverTimestamp(),
      });
      setText(""); setInAttesa([]);
      avvisa(testo, allegati.length);
    } catch (err) { addToast(messaggioErrore(err), "error"); }
    setInvio(false);
  };

  const elimina = async (c) => {
    const n = (c.allegati || []).length + (c.modifiche || []).reduce((t, m) => t + (m.allegatiTolti || []).length, 0);
    if (!await confirm(n ? `Eliminare il commento e i suoi ${n} allegati? Non si possono recuperare.` : "Eliminare il commento?", { type: "danger", confirmLabel: "Elimina" })) return;
    try {
      await deleteDoc(doc(db, "comments", c.id));
      await eliminaAllegati([...(c.allegati || []), ...(c.modifiche || []).flatMap(m => m.allegatiTolti || [])]);
    } catch (err) { addToast(messaggioErrore(err), "error"); }
  };

  const salvaModifica = async (c) => {
    const testo = modifica.testo.trim();
    const tenuti = modifica.allegati;
    if (!testo && !tenuti.length && !modifica.inAttesa.length) { addToast("Il commento non può restare vuoto: per toglierlo usa il cestino", "error"); return; }
    const tolti = (c.allegati || []).filter(a => !tenuti.some(t => t.path === a.path));
    if (testo === (c.text || "").trim() && !tolti.length && !modifica.inAttesa.length) { setModifica(null); return; }
    setInvio(true);
    try {
      const nuovi = modifica.inAttesa.length ? await caricaAllegati(modifica.inAttesa, { orgId, taskId }) : [];
      const voce = {
        il: new Date().toISOString(), da: currentUser.uid, daNome: currentUser.displayName || "",
        testoPrima: c.text || "", testoDopo: testo,
        allegatiAggiunti: nuovi.map(a => a.nome),
        allegatiTolti: tolti.map(a => ({ nome: a.nome, url: a.url, path: a.path, contentType: a.contentType || "" })),
      };
      await updateDoc(doc(db, "comments", c.id), {
        text: testo, allegati: [...tenuti, ...nuovi],
        modificato: true, modificatoIl: serverTimestamp(), modifiche: [...(c.modifiche || []), voce],
      });
      setModifica(null);
      addToast("Commento modificato: la modifica resta registrata", "success");
    } catch (err) { addToast(messaggioErrore(err), "error"); }
    setInvio(false);
  };

  const vuoto = !text.trim() && !inAttesa.length;
  return (
    <div className="detail-section">
      <div className="detail-section-title">Commenti ({comments.length})</div>
      {comments.map(c => {
        const mio = c.authorId === currentUser.uid;
        const inModifica = modifica?.id === c.id;
        const ultima = (c.modifiche || [])[c.modifiche?.length - 1];
        return (
          <div key={c.id} className="comment" data-commento={c.id}>
            <div className="comment-avatar" style={{ width: 28, height: 28, fontSize: 12 }}>{c.authorName?.[0]?.toUpperCase()}</div>
            <div className="comment-body" style={{ minWidth: 0, flex: 1 }}>
              <div className="comment-meta" style={{ flexWrap: "wrap" }}>
                <span className="comment-author">{c.authorName}</span>
                {c.createdAt && <span className="comment-time">{quando(c.createdAt)}</span>}
                {c.modificato && (
                  <button type="button" onClick={() => setStoricoAperto(s => s === c.id ? null : c.id)}
                    title="Vedi lo storico delle modifiche"
                    style={{ fontSize: 10.5, fontWeight: 700, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "0 6px", cursor: "pointer" }}>
                    ✎ modificato{ultima ? ` il ${quando(ultima.il)}` : ""}{(c.modifiche || []).length > 1 ? ` (${c.modifiche.length} volte)` : ""}
                  </button>
                )}
                {!commessaChiusa && !inModifica && (
                  <span style={{ marginLeft: "auto", display: "inline-flex", gap: 2 }}>
                    {mio && <button type="button" onClick={() => setModifica({ id: c.id, testo: c.text || "", allegati: [...(c.allegati || [])], inAttesa: [] })} className="btn-icon" aria-label="Modifica commento" title="Modifica (resta registrata)" style={{ padding: 2 }}><Pencil size={11} /></button>}
                    {(mio || userProfile?.role === "admin" || userProfile?.role === "titolare") && <button type="button" onClick={() => elimina(c)} className="btn-icon" aria-label="Elimina commento" style={{ padding: 2 }}><Trash2 size={11} /></button>}
                  </span>
                )}
              </div>
              {inModifica ? (
                <div style={{ marginTop: 4 }}>
                  <textarea className="input" aria-label="Modifica il commento" value={modifica.testo} onChange={e => setModifica(m => ({ ...m, testo: e.target.value }))} rows={3} style={{ width: "100%", resize: "vertical" }} />
                  <Allegati allegati={modifica.allegati} onTogli={a => setModifica(m => ({ ...m, allegati: m.allegati.filter(x => x.path !== a.path) }))} />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6, alignItems: "center" }}>
                    <ScegliFile inAttesa={modifica.inAttesa} setInAttesa={fn => setModifica(m => ({ ...m, inAttesa: typeof fn === "function" ? fn(m.inAttesa) : fn }))} giaAllegati={modifica.allegati.length} disabilitato={invio} />
                    <span style={{ marginLeft: "auto", display: "inline-flex", gap: 6 }}>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setModifica(null)} disabled={invio}>Annulla</button>
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => salvaModifica(c)} disabled={invio}>{invio ? "Salvo…" : "Salva modifica"}</button>
                    </span>
                  </div>
                  <div style={{ fontSize: 10.5, color: "#b45309", marginTop: 4 }}>La modifica resta registrata: tutti vedranno «✎ modificato» e potranno leggere il testo di prima.</div>
                </div>
              ) : (
                <>
                  {c.text && <div className="comment-text" style={{ whiteSpace: "pre-wrap" }}>{c.text}</div>}
                  <Allegati allegati={c.allegati || []} />
                </>
              )}
              {storicoAperto === c.id && <Storico voci={c.modifiche || []} onChiudi={() => setStoricoAperto(null)} />}
            </div>
          </div>
        );
      })}
      {!commessaChiusa && (
        <form onSubmit={invia} style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <div className="comment-avatar" style={{ width: 28, height: 28, fontSize: 12 }}>{currentUser.displayName?.[0]?.toUpperCase()}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", gap: 8 }}>
              <textarea className="input" aria-label="Scrivi un commento" value={text} onChange={e => setText(e.target.value)} placeholder="Scrivi un commento…" rows={1}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); invia(e); } }}
                style={{ flex: 1, resize: "vertical", minHeight: 36 }} />
              <button type="submit" className="btn btn-primary btn-sm" aria-label="Invia commento" disabled={invio || vuoto}>{invio ? "…" : <Send size={13} />}</button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6, alignItems: "center" }}>
              <ScegliFile inAttesa={inAttesa} setInAttesa={setInAttesa} disabilitato={invio} />
              {invio && inAttesa.length > 0 && <span style={{ fontSize: 11.5, color: "#6d28d9" }}>Carico {inAttesa.length} file…</span>}
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
